import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { generateCreatorPrompt } from '../utils/openRouter';
import CreatorResults from './CreatorResults';
import MetaTagModal from './MetaTagModal';

const { FiEdit3, FiSliders, FiHeadphones, FiZap, FiAlertCircle, FiBookOpen } = FiIcons;

const modes = [
  { id: 'simple', name: 'Simple Mode', icon: FiZap, desc: 'Best for casual ideas. AI writes everything.' },
  { id: 'advanced', name: 'Advanced Mode', icon: FiEdit3, desc: 'Full control. Generates lyrics and exclude tags.' },
  { id: 'studio', name: 'Studio Mode', icon: FiHeadphones, desc: 'Generates specific isolated instruments/sounds.' }
];

const SunoCreator = ({ settings, onRequireSettings }) => {
  const [activeMode, setActiveMode] = useState('advanced');
  const [description, setDescription] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isMetaTagModalOpen, setIsMetaTagModalOpen] = useState(false);

  const handleGenerate = async () => {
    if (!settings.apiKey) {
      onRequireSettings();
      setError("Please add your OpenRouter API key first.");
      return;
    }
    if (!description.trim()) {
      setError("Please describe the sound or song you want to create.");
      return;
    }

    setError(null);
    setResult(null);
    setIsGenerating(true);

    try {
      const promptData = await generateCreatorPrompt(settings.apiKey, settings.model, activeMode, description);
      setResult(promptData);
    } catch (err) {
      setError(err.message || "Failed to generate prompt.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto flex flex-col items-center">
      
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-stone-800 mb-2">V5.5 Prompt Architect</h2>
        <p className="text-stone-500">Build perfectly structured prompts utilizing Suno's hybrid format rules.</p>
      </div>

      {/* Mode Selector */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full mb-8">
        {modes.map((mode) => (
          <button
            key={mode.id}
            onClick={() => setActiveMode(mode.id)}
            className={`p-4 rounded-2xl border-2 text-left transition-all ${
              activeMode === mode.id 
                ? 'border-orange-500 bg-orange-50/50 shadow-md' 
                : 'border-stone-200 bg-white hover:border-orange-200'
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${activeMode === mode.id ? 'bg-orange-500 text-white' : 'bg-stone-100 text-stone-500'}`}>
                <SafeIcon icon={mode.icon} />
              </div>
              <span className={`font-bold ${activeMode === mode.id ? 'text-orange-900' : 'text-stone-700'}`}>
                {mode.name}
              </span>
            </div>
            <p className="text-xs text-stone-500">{mode.desc}</p>
          </button>
        ))}
      </div>

      {/* Input Area */}
      <div className="w-full bg-white rounded-3xl shadow-sm border border-stone-200 p-2 mb-4">
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe your song... (e.g., A fast-paced 80s synthwave track with a driving bassline, retro drum machines, and a soaring clean female vocal about driving through a neon city.)"
          className="w-full h-40 p-4 resize-none outline-none text-stone-700 bg-transparent placeholder-stone-400"
        />
        
        <div className="flex justify-between items-center p-2 border-t border-stone-100 mt-2">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsMetaTagModalOpen(true)}
              className="text-xs text-orange-700 hover:text-white hover:bg-orange-600 flex items-center gap-1.5 font-bold transition-colors bg-orange-50 px-3 py-1.5 rounded-lg border border-orange-200"
            >
              <SafeIcon icon={FiBookOpen} /> Tag Dictionary
            </button>
            <div className="text-xs text-stone-400 items-center gap-2 hidden sm:flex">
              <SafeIcon icon={FiSliders} /> Auto-applying V5.5 Structure Rules
            </div>
          </div>
          
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="px-6 py-2.5 bg-stone-900 text-white rounded-xl font-bold hover:bg-stone-800 transition-all disabled:opacity-70 flex items-center gap-2"
          >
            {isGenerating ? (
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
                <SafeIcon icon={FiIcons.FiRefreshCw} />
              </motion.div>
            ) : (
              <>Generate <SafeIcon icon={FiIcons.FiArrowRight} /></>
            )}
          </button>
        </div>
      </div>

      {error && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full p-4 bg-red-50 text-red-600 rounded-xl flex items-center gap-3">
          <SafeIcon icon={FiAlertCircle} className="text-xl" />
          <p className="text-sm font-medium">{error}</p>
        </motion.div>
      )}

      {/* Results */}
      {result && !isGenerating && <CreatorResults result={result} />}

      <MetaTagModal 
        isOpen={isMetaTagModalOpen} 
        onClose={() => setIsMetaTagModalOpen(false)} 
      />
    </div>
  );
};

export default SunoCreator;